/**
 * Move keyboard focus to the start of the page's main content.
 *
 * Targets the page <h1> inside the main landmark rather than <main> itself:
 * <main> spans the full viewport width from the top of the page, so an
 * outline drawn around it lands off-screen and under the fixed header, and
 * the focus is invisible (WCAG 2.4.7 / 2.4.11 — NCS-255). The heading is a
 * small, on-screen target whose ring is clearly visible, and screen readers
 * announce it ("heading level 1, …") as the start of the content. Falls back
 * to <main> on a page without an <h1>.
 *
 * `scroll: true` brings the target into view (skip link). Route changes pass
 * `false` because RouteFocus resets the scroll position itself.
 */
export function focusMainContent({ scroll = true }: { scroll?: boolean } = {}): HTMLElement | null {
  const main = document.getElementById("main-content");
  const target = main?.querySelector<HTMLElement>("h1") ?? main;
  if (!target) return null;

  // tabindex=-1 lets a non-interactive element take programmatic focus
  // without joining the tab order.
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  target.focus({ preventScroll: true });
  if (scroll) target.scrollIntoView({ block: "start" });
  return target;
}
